import React, { useCallback, useReducer } from "react";

import { clsx } from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { Alert, Divider } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { AccountReduxState } from "@app/modules/account";
import type { NapiUser } from "@app/types/napi-types";
import { redirect } from "@app/utils/window";

import { BillingAddressSection } from "./billing-address-section";
import { EmailSection } from "./email-section";
import { LanguageSection } from "./language-section";
import { NewsletterSection } from "./newsletter-section";
import { PasswordSection } from "./password-section";
import { PaymentMethodsSection } from "./payment-methods-section";
import {
  getErrorModalConfig,
  toBusbudError
} from "../../../../helpers/api-error";
import { updateUser, updateUserError } from "../../../../store/state/user";
import { confirmAccountUpdate } from "../../../../tracking/account-tracking";
import { AccountLayout } from "../account-layout";
import { AccountVerification } from "../account-verification";

interface Props {
  show_success?: boolean;
}

interface State {
  error: string | null;
  success: boolean;
}

type Action =
  | { type: "reset" }
  | { type: "error"; error: string | null }
  | { type: "success" };

function reducer(state: State, action: Action) {
  switch (action.type) {
    case "reset":
      return { ...state, error: null, success: false };
    case "error":
      if (action.error) {
        return { ...state, error: action.error, success: false };
      } else {
        return { ...state, error: null, success: false };
      }
    case "success":
      return { ...state, error: null, success: true };
    default:
      return state;
  }
}

export const SettingsApp: React.FC<Props> = ({ show_success = false }) => {
  const user = useSelector<AccountReduxState, NapiUser>(state => state.user);

  const [state, inner_dispatch] = useReducer(reducer, {
    error: null,
    success: show_success
  });

  const dispatch = useDispatch();

  const { api, tracker, translator, sentry, urlBuilder, locale } =
    useAppContext();

  const handleChange = useCallback(
    (new_values: { email_opt_in: boolean } | { locale: string }) => {
      inner_dispatch({ type: "reset" });

      const new_user = { ...user, ...new_values };
      // Optimistic response
      dispatch(updateUser(new_user));

      api.updateUser(user.id, new_user).catch((err: Error | null) => {
        dispatch(updateUserError(err));

        inner_dispatch({
          type: "error",
          error: getErrorModalConfig(
            translator,
            sentry,
            toBusbudError(err),
            locale
          ).text
        });

        // Reset user's settings
        api.getUser(user.id).then(u => {
          dispatch(updateUser(u));
        });
      });
    },
    [api, dispatch, locale, sentry, translator, user]
  );

  const onChangeNewsletter = useCallback(
    (opt_in: { email_opt_in: boolean }) => {
      tracker.track(
        confirmAccountUpdate("settings", "newsletter_optin", `${opt_in}`)
      );
      handleChange(opt_in);
    },
    [handleChange, tracker]
  );

  const onEmailLanguageChange = useCallback(
    (selected_language: { locale: string }) => {
      tracker.track(confirmAccountUpdate("settings", "email_language"));
      handleChange(selected_language);
    },
    [handleChange, tracker]
  );

  return (
    <AccountLayout current_page="settings">
      <div className="flex flex-col gap-500">
        <AccountVerification user={user} />

        <div className={clsx({ hidden: !state.error && !state.success })}>
          {!!state.error && (
            <Alert severity="negative">
              <Alert.Description>{state.error}</Alert.Description>
            </Alert>
          )}
          {!!state.success && (
            <Alert
              severity="positive"
              onClose={() => inner_dispatch({ type: "reset" })}
              closeButtonAriaLabel={translator.t("!modal.cta.close")}
            >
              <Alert.Description>
                {translator.t("!accounts.settings.save-information-success")}
              </Alert.Description>
            </Alert>
          )}
        </div>

        <PaymentMethodsSection
          onError={error => inner_dispatch({ type: "error", error })}
          onSuccess={() => inner_dispatch({ type: "success" })}
        />
        <BillingAddressSection
          onError={error => inner_dispatch({ type: "error", error })}
          onSuccess={() => inner_dispatch({ type: "success" })}
        />
        <Divider />
        <EmailSection onSuccess={() => inner_dispatch({ type: "success" })} />
        <PasswordSection
          onSuccess={() => {
            // reload required to update the cookie's value
            // previous was invalidated for security reasons
            redirect(urlBuilder.accountSettings("success"));
          }}
        />
        <Divider />
        <LanguageSection
          user_locale={user.locale}
          onChange={onEmailLanguageChange}
        />
        <NewsletterSection
          email_opt_in={user.email_opt_in}
          onChange={onChangeNewsletter}
        />
      </div>
    </AccountLayout>
  );
};
